import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { JOB_CATEGORIES, JobCategory } from '@/lib/types/job';
import { format, startOfMonth, startOfWeek, differenceInDays, subDays } from 'date-fns';

export interface SpendKPIs {
  totalSpendCents: number;
  jobCount: number;
  avgJobValueCents: number;
  medianJobValueCents: number;
  activeJobsCount: number;
  activeJobsByStatus: {
    bidding: number;
    production: number;
    other: number;
  };
  uniqueVendorCount: number;
  newVendorCount: number;
  prevTotalSpendCents: number;
  prevAvgJobValueCents: number;
  prevVendorCount: number;
}

export interface CategorySpend {
  category: JobCategory;
  label: string;
  spendCents: number;
  spend: number;
  jobCount: number;
  percentage: number;
}

export interface VendorSpend {
  vendorId: string;
  vendorName: string;
  spendCents: number;
  spend: number;
  jobCount: number;
  isOther?: boolean;
}

export interface SpendPeriod {
  period: string;
  periodStart: Date;
  spendCents: number;
  spend: number;
  jobCount: number;
}

export interface RequesterSpend {
  userId: string;
  name: string;
  jobCount: number;
  totalSpendCents: number;
  totalSpend: number;
  avgJobValue: number;
  percentage: number;
}

export interface SpendExportData {
  categories: CategorySpend[];
  vendors: VendorSpend[];
  trends: SpendPeriod[];
  requesters: RequesterSpend[];
  jobs: Array<{
    jobId: string;
    publicId: string;
    title: string;
    category: string;
    vendorName: string;
    amountCents: number;
    amount: number;
    completedDate: string;
    requesterName: string;
  }>;
}

function getCategoryLabel(category: string): string {
  const found = JOB_CATEGORIES.find(c => c.value === category);
  return found?.label || 'Other';
}

export function useSpendByCategory(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined
) {
  return useQuery({
    queryKey: ['spend-by-category', startDate.toISOString(), endDate.toISOString(), organizationId],
    queryFn: async (): Promise<CategorySpend[]> => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id,
          category,
          purchase_order:purchase_orders!left(po_amount_cents)
        `)
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (error) throw error;

      // Aggregate by category
      const categoryMap = new Map<string, { spendCents: number; jobCount: number }>();
      let totalSpend = 0;

      for (const job of data || []) {
        const category = job.category || 'other';
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        const amount = po?.po_amount_cents || 0;
        
        const existing = categoryMap.get(category) || { spendCents: 0, jobCount: 0 };
        categoryMap.set(category, {
          spendCents: existing.spendCents + amount,
          jobCount: existing.jobCount + 1,
        });
        totalSpend += amount;
      }

      // Convert to array and calculate percentages
      const result: CategorySpend[] = Array.from(categoryMap.entries())
        .map(([category, data]) => ({
          category: category as JobCategory,
          label: getCategoryLabel(category),
          spendCents: data.spendCents,
          spend: data.spendCents / 100,
          jobCount: data.jobCount,
          percentage: totalSpend > 0 ? (data.spendCents / totalSpend) * 100 : 0,
        }))
        .sort((a, b) => b.spendCents - a.spendCents);

      return result;
    },
    enabled: !!organizationId,
  });
}

export function useSpendByVendor(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined
) {
  return useQuery({
    queryKey: ['spend-by-vendor', startDate.toISOString(), endDate.toISOString(), organizationId],
    queryFn: async (): Promise<VendorSpend[]> => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id,
          purchase_order:purchase_orders!left(
            po_amount_cents,
            vendor_organization_id
          )
        `)
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (error) throw error;

      // Collect vendor IDs
      const vendorIds = new Set<string>();
      const vendorMap = new Map<string, { spendCents: number; jobCount: number }>();

      for (const job of data || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (!po?.vendor_organization_id) continue;
        
        vendorIds.add(po.vendor_organization_id);
        const existing = vendorMap.get(po.vendor_organization_id) || { spendCents: 0, jobCount: 0 };
        vendorMap.set(po.vendor_organization_id, {
          spendCents: existing.spendCents + (po.po_amount_cents || 0),
          jobCount: existing.jobCount + 1,
        });
      }

      // Fetch vendor names
      const vendorNamesMap = new Map<string, string>();
      if (vendorIds.size > 0) {
        const { data: orgs } = await supabase
          .from('organizations')
          .select('id, name')
          .in('id', Array.from(vendorIds));
        
        for (const org of orgs || []) {
          vendorNamesMap.set(org.id, org.name);
        }
      }

      // Convert to array and sort
      let vendors: VendorSpend[] = Array.from(vendorMap.entries())
        .map(([vendorId, data]) => ({
          vendorId,
          vendorName: vendorNamesMap.get(vendorId) || 'Unknown Vendor',
          spendCents: data.spendCents,
          spend: data.spendCents / 100,
          jobCount: data.jobCount,
        }))
        .sort((a, b) => b.spendCents - a.spendCents);

      // If more than 10, aggregate "All Others"
      if (vendors.length > 10) {
        const top10 = vendors.slice(0, 10);
        const others = vendors.slice(10);
        const othersTotal = others.reduce((sum, v) => sum + v.spendCents, 0);
        const othersCount = others.reduce((sum, v) => sum + v.jobCount, 0);
        
        vendors = [
          ...top10,
          {
            vendorId: 'others',
            vendorName: 'All Others',
            spendCents: othersTotal,
            spend: othersTotal / 100,
            jobCount: othersCount,
            isOther: true,
          },
        ];
      }

      return vendors;
    },
    enabled: !!organizationId,
  });
}

export function useSpendTrend(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined
) {
  return useQuery({
    queryKey: ['spend-trend', startDate.toISOString(), endDate.toISOString(), organizationId],
    queryFn: async (): Promise<SpendPeriod[]> => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id,
          created_at,
          purchase_order:purchase_orders!left(po_amount_cents)
        `)
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (error) throw error;

      // Determine bucket size (weekly if < 60 days, monthly otherwise)
      const daysDiff = differenceInDays(endDate, startDate);
      const useWeekly = daysDiff < 60;

      // Aggregate by period
      const periodMap = new Map<string, { periodStart: Date; spendCents: number; jobCount: number }>();

      for (const job of data || []) {
        const date = new Date(job.created_at);
        const periodStart = useWeekly ? startOfWeek(date, { weekStartsOn: 1 }) : startOfMonth(date);
        const periodKey = periodStart.toISOString();
        
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        const amount = po?.po_amount_cents || 0;
        
        const existing = periodMap.get(periodKey) || { periodStart, spendCents: 0, jobCount: 0 };
        periodMap.set(periodKey, {
          periodStart,
          spendCents: existing.spendCents + amount,
          jobCount: existing.jobCount + 1,
        });
      }

      // Convert to array and sort by date
      const result: SpendPeriod[] = Array.from(periodMap.values())
        .map(data => ({
          period: useWeekly 
            ? `Week of ${format(data.periodStart, 'MMM d')}` 
            : format(data.periodStart, 'MMM yyyy'),
          periodStart: data.periodStart,
          spendCents: data.spendCents,
          spend: data.spendCents / 100,
          jobCount: data.jobCount,
        }))
        .sort((a, b) => a.periodStart.getTime() - b.periodStart.getTime());

      return result;
    },
    enabled: !!organizationId,
  });
}

export function useSpendByRequester(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined,
  currentUserId: string | undefined,
  isAdmin: boolean
) {
  return useQuery({
    queryKey: ['spend-by-requester', startDate.toISOString(), endDate.toISOString(), organizationId, currentUserId, isAdmin],
    queryFn: async (): Promise<RequesterSpend[]> => {
      if (!organizationId) return [];

      let query = supabase
        .from('jobs')
        .select(`
          id,
          created_by_user_id,
          purchase_order:purchase_orders!left(po_amount_cents)
        `)
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      // Non-admins only see their own data
      if (!isAdmin && currentUserId) {
        query = query.eq('created_by_user_id', currentUserId);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Collect user IDs and aggregate
      const userIds = new Set<string>();
      const requesterMap = new Map<string, { spendCents: number; jobCount: number }>();
      let totalSpend = 0;

      for (const job of data || []) {
        if (!job.created_by_user_id) continue;
        
        userIds.add(job.created_by_user_id);
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        const amount = po?.po_amount_cents || 0;
        
        const existing = requesterMap.get(job.created_by_user_id) || { spendCents: 0, jobCount: 0 };
        requesterMap.set(job.created_by_user_id, {
          spendCents: existing.spendCents + amount,
          jobCount: existing.jobCount + 1,
        });
        totalSpend += amount;
      }

      // Fetch user names from client_profiles
      const userNamesMap = new Map<string, string>();
      if (userIds.size > 0) {
        const { data: profiles } = await supabase
          .from('client_profiles')
          .select('user_id, first_name, last_name')
          .in('user_id', Array.from(userIds));
        
        for (const profile of profiles || []) {
          const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Unknown';
          userNamesMap.set(profile.user_id, name);
        }
      }

      // Convert to array
      const result: RequesterSpend[] = Array.from(requesterMap.entries())
        .map(([userId, data]) => ({
          userId,
          name: userNamesMap.get(userId) || 'Unknown User',
          jobCount: data.jobCount,
          totalSpendCents: data.spendCents,
          totalSpend: data.spendCents / 100,
          avgJobValue: data.jobCount > 0 ? (data.spendCents / 100) / data.jobCount : 0,
          percentage: totalSpend > 0 ? (data.spendCents / totalSpend) * 100 : 0,
        }))
        .sort((a, b) => b.totalSpendCents - a.totalSpendCents);

      return result;
    },
    enabled: !!organizationId,
  });
}

export function useSpendExportData(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined
) {
  return useQuery({
    queryKey: ['spend-export', startDate.toISOString(), endDate.toISOString(), organizationId],
    queryFn: async (): Promise<SpendExportData['jobs']> => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id,
          public_id,
          title,
          category,
          created_by_user_id,
          updated_at,
          purchase_order:purchase_orders!left(
            po_amount_cents,
            vendor_organization_id
          )
        `)
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (error) throw error;

      // Collect IDs for lookups
      const vendorIds = new Set<string>();
      const userIds = new Set<string>();

      for (const job of data || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (po?.vendor_organization_id) vendorIds.add(po.vendor_organization_id);
        if (job.created_by_user_id) userIds.add(job.created_by_user_id);
      }

      // Fetch vendor names
      const vendorNamesMap = new Map<string, string>();
      if (vendorIds.size > 0) {
        const { data: orgs } = await supabase
          .from('organizations')
          .select('id, name')
          .in('id', Array.from(vendorIds));
        for (const org of orgs || []) {
          vendorNamesMap.set(org.id, org.name);
        }
      }

      // Fetch user names
      const userNamesMap = new Map<string, string>();
      if (userIds.size > 0) {
        const { data: profiles } = await supabase
          .from('client_profiles')
          .select('user_id, first_name, last_name')
          .in('user_id', Array.from(userIds));
        for (const profile of profiles || []) {
          const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Unknown';
          userNamesMap.set(profile.user_id, name);
        }
      }

      // Map to export format
      return (data || []).map(job => {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        return {
          jobId: job.id,
          publicId: job.public_id,
          title: job.title,
          category: getCategoryLabel(job.category || 'other'),
          vendorName: po?.vendor_organization_id ? vendorNamesMap.get(po.vendor_organization_id) || 'Unknown' : 'N/A',
          amountCents: po?.po_amount_cents || 0,
          amount: (po?.po_amount_cents || 0) / 100,
          completedDate: format(new Date(job.updated_at), 'yyyy-MM-dd'),
          requesterName: job.created_by_user_id ? userNamesMap.get(job.created_by_user_id) || 'Unknown' : 'Unknown',
        };
      });
    },
    enabled: !!organizationId,
  });
}

export function useSpendKPIs(
  startDate: Date,
  endDate: Date,
  organizationId: string | undefined
) {
  return useQuery({
    queryKey: ['spend-kpis', startDate.toISOString(), endDate.toISOString(), organizationId],
    queryFn: async (): Promise<SpendKPIs> => {
      if (!organizationId) {
        return {
          totalSpendCents: 0,
          jobCount: 0,
          avgJobValueCents: 0,
          medianJobValueCents: 0,
          activeJobsCount: 0,
          activeJobsByStatus: { bidding: 0, production: 0, other: 0 },
          uniqueVendorCount: 0,
          newVendorCount: 0,
          prevTotalSpendCents: 0,
          prevAvgJobValueCents: 0,
          prevVendorCount: 0,
        };
      }

      // Calculate previous period (same duration before start date)
      const periodDays = differenceInDays(endDate, startDate);
      const prevStartDate = subDays(startDate, periodDays);
      const prevEndDate = subDays(startDate, 1);

      // Fetch current period completed jobs with POs
      const { data: currentJobs, error: currentError } = await supabase
        .from('jobs')
        .select(`
          id,
          purchase_order:purchase_orders!left(
            po_amount_cents,
            vendor_organization_id
          )
        `)
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (currentError) throw currentError;

      // Fetch previous period jobs
      const { data: prevJobs, error: prevError } = await supabase
        .from('jobs')
        .select(`
          id,
          purchase_order:purchase_orders!left(
            po_amount_cents,
            vendor_organization_id
          )
        `)
        .eq('organization_id', organizationId)
        .gte('created_at', prevStartDate.toISOString())
        .lte('created_at', prevEndDate.toISOString())
        .in('status', ['accepted', 'closed', 'in_progress', 'completed_pending_review', 'awarded']);

      if (prevError) throw prevError;

      // Fetch active jobs (not closed or cancelled)
      const { data: activeJobs, error: activeError } = await supabase
        .from('jobs')
        .select('id, status')
        .eq('organization_id', organizationId)
        .not('status', 'in', '("closed","cancelled")');

      if (activeError) throw activeError;

      // Calculate current period metrics
      const currentAmounts: number[] = [];
      const currentVendorIds = new Set<string>();

      for (const job of currentJobs || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (po?.po_amount_cents) {
          currentAmounts.push(po.po_amount_cents);
        }
        if (po?.vendor_organization_id) {
          currentVendorIds.add(po.vendor_organization_id);
        }
      }

      const totalSpendCents = currentAmounts.reduce((sum, a) => sum + a, 0);
      const jobCount = currentAmounts.length;
      const avgJobValueCents = jobCount > 0 ? Math.round(totalSpendCents / jobCount) : 0;

      // Calculate median
      const sortedAmounts = [...currentAmounts].sort((a, b) => a - b);
      let medianJobValueCents = 0;
      if (sortedAmounts.length > 0) {
        const mid = Math.floor(sortedAmounts.length / 2);
        medianJobValueCents = sortedAmounts.length % 2 === 0
          ? Math.round((sortedAmounts[mid - 1] + sortedAmounts[mid]) / 2)
          : sortedAmounts[mid];
      }

      // Calculate previous period metrics
      const prevAmounts: number[] = [];
      const prevVendorIds = new Set<string>();

      for (const job of prevJobs || []) {
        const po = Array.isArray(job.purchase_order) ? job.purchase_order[0] : job.purchase_order;
        if (po?.po_amount_cents) {
          prevAmounts.push(po.po_amount_cents);
        }
        if (po?.vendor_organization_id) {
          prevVendorIds.add(po.vendor_organization_id);
        }
      }

      const prevTotalSpendCents = prevAmounts.reduce((sum, a) => sum + a, 0);
      const prevJobCount = prevAmounts.length;
      const prevAvgJobValueCents = prevJobCount > 0 ? Math.round(prevTotalSpendCents / prevJobCount) : 0;

      // Calculate new vendors (in current but not in previous)
      const newVendorCount = Array.from(currentVendorIds).filter(id => !prevVendorIds.has(id)).length;

      // Calculate active jobs breakdown
      const biddingStatuses = ['draft', 'rfq_open', 'rfq_closed'];
      const productionStatuses = ['awarded', 'in_progress', 'accepted'];
      
      let bidding = 0;
      let production = 0;
      let other = 0;

      for (const job of activeJobs || []) {
        if (biddingStatuses.includes(job.status)) {
          bidding++;
        } else if (productionStatuses.includes(job.status)) {
          production++;
        } else {
          other++;
        }
      }

      return {
        totalSpendCents,
        jobCount,
        avgJobValueCents,
        medianJobValueCents,
        activeJobsCount: (activeJobs || []).length,
        activeJobsByStatus: { bidding, production, other },
        uniqueVendorCount: currentVendorIds.size,
        newVendorCount,
        prevTotalSpendCents,
        prevAvgJobValueCents,
        prevVendorCount: prevVendorIds.size,
      };
    },
    enabled: !!organizationId,
  });
}
